<template>
  <div>
    <AppTitulo titulo="Integrações do pregão" />

    <section class="section">
      <div class="container is-fluid">
        <div class="columns">
          <div class="column is-10 is-offset-1">
            <div class="box">
              <b-field grouped>
                <b-field expanded label="ID do pregão" message="OfertasCompra.id">
                  <b-input
                    v-model.trim="procurementId"
                    type="number"
                    min="1"
                    placeholder="Ex.: 3272"
                    @keyup.native.enter.prevent="handleSearch"
                  />
                </b-field>
                <b-field label="&nbsp;">
                  <b-button
                    type="is-primary"
                    :loading="loading.search"
                    @click.prevent="handleSearch"
                  >
                    Consultar
                  </b-button>
                </b-field>
              </b-field>
            </div>

            <div v-if="dossier" class="box">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h2 class="title is-5 mb-0">Pregão #{{ dossier.procurement.id }}</h2>
                <b-button
                  size="is-small"
                  type="is-text"
                  :loading="loading.search || loading.poll"
                  @click.prevent="refreshDossier"
                >
                  Atualizar
                </b-button>
              </div>
              <div class="columns is-multiline">
                <div class="column is-3">
                  <p class="heading">Processo</p>
                  <p>{{ dossier.procurement.processNumber || '—' }}</p>
                </div>
                <div class="column is-3">
                  <p class="heading">OC</p>
                  <p>{{ dossier.procurement.number }}</p>
                </div>
                <div class="column is-3">
                  <p class="heading">Órgão</p>
                  <p>
                    {{ dossier.procurement.companyName }} (#{{ dossier.procurement.companyId }})
                  </p>
                </div>
                <div class="column is-3">
                  <p class="heading">Origem</p>
                  <p>{{ originLabel }}</p>
                </div>
              </div>
            </div>

            <div v-if="dossier" class="box">
              <h2 class="title is-6">Termos</h2>
              <p class="is-size-7 has-text-grey mb-3">
                A regeração usa o responsável do pregão para assinar. O PDF atual permanece até o
                job concluir.
              </p>
              <p v-if="loading.terms" class="has-text-grey">Carregando termos...</p>
              <div v-else-if="terms" class="columns">
                <div v-for="term in termCards" :key="term.tipo" class="column is-6">
                  <p class="heading">{{ term.titulo }}</p>
                  <p>
                    <strong>Arquivo:</strong>
                    {{ term.arquivo || 'Ainda não gerado' }}
                  </p>
                  <p v-if="term.motivo" class="has-text-danger is-size-7 mt-2">
                    {{ term.motivo }}
                  </p>
                  <b-button
                    class="mt-3"
                    size="is-small"
                    type="is-primary"
                    :disabled="!term.podeRegerar"
                    :loading="regeneratingTerm === term.tipo"
                    @click.prevent="handleRegenerateTerm(term)"
                  >
                    {{ term.gerado ? 'Regerar' : 'Gerar' }}
                  </b-button>
                </div>
              </div>
              <p v-else class="has-text-grey">Não foi possível carregar os termos.</p>
            </div>

            <div v-if="dossier" class="columns">
              <div
                v-for="destination in dossier.destinations"
                :key="destination.destination"
                class="column is-6"
              >
                <div class="box">
                  <div class="is-flex is-justify-content-space-between is-align-items-center mb-2">
                    <h3 class="title is-6 mb-0">{{ destinationLabel(destination.destination) }}</h3>
                    <b-tag :type="statusType(destination.currentStatus)">
                      {{ destination.currentStatus || 'SEM ENVIO' }}
                    </b-tag>
                  </div>
                  <p class="mb-2">
                    <b-tag :type="destination.enabled ? 'is-success' : 'is-light'">
                      {{ destination.enabled ? 'Flag ligada' : 'Flag desligada' }}
                    </b-tag>
                    <b-tag type="is-light">{{
                      resourceTypesLabel(destination.resourceTypes)
                    }}</b-tag>
                  </p>
                  <p>
                    <strong>idExterno:</strong>
                    {{ destination.externalId || '—' }}
                  </p>
                  <p
                    v-if="destination.lastError"
                    class="has-text-danger is-size-7 destination-last-error"
                    :title="destination.lastError"
                  >
                    {{ destination.lastError }}
                  </p>
                  <p class="is-size-7 has-text-grey">
                    Filas: {{ (destination.queues || []).join(', ') || '—' }}
                  </p>
                  <p class="is-size-7 mt-2">
                    Itens: {{ destinationItemSummary(destination.destination) }} · Documentos:
                    {{ destinationDocumentSummary(destination.destination) }}
                  </p>
                  <div class="buttons mt-3">
                    <b-button
                      size="is-small"
                      :type="destination.currentStatus === 'ERRO' ? 'is-primary' : 'is-light'"
                      :loading="
                        isReprocessing(
                          destination.destination,
                          'PROCUREMENT',
                          dossier.procurement.id,
                        )
                      "
                      :disabled="!destination.supported"
                      @click.prevent="handleReprocess(publicationFromDestination(destination))"
                    >
                      Reprocessar compra
                    </b-button>
                    <b-button
                      v-if="supportsResource(destination, 'PROCUREMENT_ITEM')"
                      size="is-small"
                      type="is-light"
                      :loading="isBatchReprocessing(destination.destination, 'PROCUREMENT_ITEM')"
                      :disabled="!destination.supported"
                      @click.prevent="
                        handleBatchReprocess(destination.destination, 'PROCUREMENT_ITEM', false)
                      "
                    >
                      Reprocessar itens da compra
                    </b-button>
                    <b-button
                      v-if="supportsResource(destination, 'PROCUREMENT_DOCUMENT')"
                      size="is-small"
                      type="is-light"
                      :loading="
                        isBatchReprocessing(destination.destination, 'PROCUREMENT_DOCUMENT')
                      "
                      :disabled="!destination.supported"
                      @click.prevent="
                        handleBatchReprocess(destination.destination, 'PROCUREMENT_DOCUMENT', false)
                      "
                    >
                      Reprocessar documentos da compra
                    </b-button>
                    <b-button
                      v-if="destinationErrorCount(destination.destination, 'PROCUREMENT_ITEM') > 0"
                      size="is-small"
                      type="is-text"
                      :loading="isBatchReprocessing(destination.destination, 'PROCUREMENT_ITEM')"
                      @click.prevent="
                        handleBatchReprocess(destination.destination, 'PROCUREMENT_ITEM', true)
                      "
                    >
                      Só itens com erro
                    </b-button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="dossier" class="box">
              <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
                <h2 class="title is-6 mb-0">
                  Publicações
                  <span class="has-text-grey has-text-weight-normal">
                    ({{ filteredPublications.length }}/{{ publications.length }})
                  </span>
                </h2>
                <div class="buttons mb-0">
                  <b-button
                    v-for="filter in publicationFilters"
                    :key="filter.id"
                    size="is-small"
                    :type="publicationFilter === filter.id ? 'is-primary' : 'is-light'"
                    @click.prevent="publicationFilter = filter.id"
                  >
                    {{ filter.label }}
                  </b-button>
                </div>
              </div>
              <b-table
                :data="filteredPublications"
                :mobile-card="false"
                :paginated="filteredPublications.length > 10"
                :per-page="10"
              >
                <b-table-column field="destination" label="Destino" v-slot="props">
                  {{ destinationLabel(props.row.destination) }}
                </b-table-column>
                <b-table-column field="label" label="Recurso" v-slot="props">
                  {{ props.row.label || resourceLabel(props.row.resourceType) }}
                </b-table-column>
                <b-table-column field="entityId" label="ID" numeric v-slot="props">
                  {{ props.row.entityId }}
                </b-table-column>
                <b-table-column field="currentStatus" label="Status" v-slot="props">
                  <b-tag :type="statusType(props.row.currentStatus)">
                    {{ props.row.currentStatus }}
                  </b-tag>
                </b-table-column>
                <b-table-column field="externalId" label="Externo" v-slot="props">
                  {{ props.row.externalId || '—' }}
                </b-table-column>
                <b-table-column field="lastError" label="Último erro" v-slot="props">
                  <span
                    class="destination-last-error"
                    :class="{ 'has-text-danger': props.row.lastError }"
                    :title="props.row.lastError || ''"
                  >
                    {{ props.row.lastError || '—' }}
                  </span>
                </b-table-column>
                <b-table-column label="Ação" v-slot="props">
                  <b-button
                    size="is-small"
                    type="is-text"
                    :loading="
                      isReprocessing(
                        props.row.destination,
                        props.row.resourceType,
                        props.row.entityId,
                      )
                    "
                    :disabled="!props.row.supported"
                    @click.prevent="handleReprocess(props.row)"
                  >
                    Reprocessar
                  </b-button>
                </b-table-column>
                <template #empty>
                  <div class="has-text-centered has-text-grey">Nenhuma publicação persistida.</div>
                </template>
              </b-table>
            </div>

            <div v-if="dossier" class="box">
              <h2 class="title is-6">Inbox de eventos</h2>
              <b-table
                :data="dossier.inbox || []"
                :mobile-card="false"
                :paginated="(dossier.inbox || []).length > 8"
                :per-page="8"
              >
                <b-table-column field="createdAt" label="Quando" v-slot="props">
                  {{ formatDateTime(props.row.createdAt) }}
                </b-table-column>
                <b-table-column field="eventName" label="Evento" v-slot="props">
                  {{ props.row.eventName }}
                </b-table-column>
                <b-table-column field="status" label="Inbox" v-slot="props">
                  <b-tag :type="inboxStatusType(props.row.status)">
                    {{ props.row.status }}
                  </b-tag>
                </b-table-column>
                <b-table-column field="correlationId" label="Correlation" v-slot="props">
                  <code>{{ props.row.correlationId }}</code>
                </b-table-column>
                <template #empty>
                  <div class="has-text-centered has-text-grey">Nenhum evento na inbox.</div>
                </template>
              </b-table>
            </div>

            <p class="has-text-grey is-size-7">
              ARP e entityType manual:
              <router-link :to="{ name: 'AdminPncpReprocess' }"
                >reprocesso avançado PNCP</router-link
              >.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import AppTitulo from '@/components/AppTitulo';

const DESTINATION_LABELS = {
  PNCP: 'PNCP',
  MAISBRASIL: '+Brasil',
};

const RESOURCE_LABELS = {
  PROCUREMENT: 'Compra',
  PROCUREMENT_ITEM: 'Item',
  PROCUREMENT_DOCUMENT: 'Documento',
};

const STATUS_RANK = {
  ERRO: 0,
  PROCESSANDO: 1,
  PENDENTE: 2,
  INTEGRADO: 3,
  CANCELADO: 4,
};

const IN_FLIGHT_STATUSES = ['PENDENTE', 'PROCESSANDO'];
const STATUS_POLL_INTERVAL_MS = 2500;
const STATUS_POLL_TIMEOUT_MS = 60000;

export default {
  name: 'AdminProcurementIntegrations',
  components: {
    AppTitulo,
  },
  data() {
    return {
      procurementId: '',
      dossier: null,
      terms: null,
      publicationFilter: 'ALL',
      loading: {
        search: false,
        poll: false,
        terms: false,
      },
      regeneratingTerm: '',
      reprocessingKey: '',
      batchReprocessingKey: '',
      statusPollTimer: null,
      statusPollToken: 0,
    };
  },
  computed: {
    originLabel() {
      if (!this.dossier || !this.dossier.procurement) return '—';
      if (this.dossier.procurement.isTvu) return 'TVU';
      const origin = this.dossier.procurement.resourcesOrigin || [];
      return origin.length ? origin.join(', ') : '—';
    },
    publications() {
      if (!this.dossier || !this.dossier.publications) return [];
      return this.dossier.publications;
    },
    publicationFilters() {
      return [
        { id: 'ALL', label: 'Todos' },
        { id: 'PROCUREMENT_ITEM', label: 'Itens' },
        { id: 'PROCUREMENT_DOCUMENT', label: 'Documentos' },
        { id: 'PROCUREMENT', label: 'Compra' },
      ];
    },
    termCards() {
      if (!this.terms) return [];
      return [
        { tipo: 'adjudicacao', titulo: 'Adjudicação', ...this.terms.adjudicacao },
        { tipo: 'homologacao', titulo: 'Homologação', ...this.terms.homologacao },
      ];
    },
    filteredPublications() {
      const rows =
        this.publicationFilter === 'ALL'
          ? this.publications.slice()
          : this.publications.filter(row => row.resourceType === this.publicationFilter);

      return rows.sort((left, right) => {
        const leftRank =
          STATUS_RANK[left.currentStatus] !== undefined ? STATUS_RANK[left.currentStatus] : 9;
        const rightRank =
          STATUS_RANK[right.currentStatus] !== undefined ? STATUS_RANK[right.currentStatus] : 9;
        if (leftRank !== rightRank) return leftRank - rightRank;

        const leftIdentifier = left.identifier || left.entityId;
        const rightIdentifier = right.identifier || right.entityId;
        return leftIdentifier - rightIdentifier;
      });
    },
  },
  created() {
    const queryId = this.$route.query.id;
    if (queryId) {
      this.procurementId = String(queryId);
      this.handleSearch();
    }
  },
  beforeDestroy() {
    this.stopStatusPoll();
  },
  methods: {
    ...mapActions('integrations', [
      'fetchAuctionTerms',
      'fetchProcurementDossier',
      'regenerateAuctionTerm',
      'reprocessIntegration',
      'reprocessIntegrationBatch',
    ]),
    formatDateTime(value) {
      if (!value) return '—';
      return new Date(value).toLocaleString('pt-BR');
    },
    destinationLabel(destination) {
      return DESTINATION_LABELS[destination] || destination;
    },
    resourceLabel(resourceType) {
      return RESOURCE_LABELS[resourceType] || resourceType;
    },
    resourceTypesLabel(resourceTypes) {
      if (!resourceTypes || !resourceTypes.length) return 'Sem handler';
      return resourceTypes.map(this.resourceLabel).join(' + ');
    },
    supportsResource(destination, resourceType) {
      return destination.resourceTypes && destination.resourceTypes.indexOf(resourceType) !== -1;
    },
    publicationsOf(destination, resourceType) {
      return this.publications.filter(
        row => row.destination === destination && row.resourceType === resourceType,
      );
    },
    destinationErrorCount(destination, resourceType) {
      return this.publicationsOf(destination, resourceType).filter(
        row => row.currentStatus === 'ERRO',
      ).length;
    },
    destinationItemSummary(destination) {
      const rows = this.publicationsOf(destination, 'PROCUREMENT_ITEM');
      if (!rows.length) return 'sem publicação';
      const errors = rows.filter(row => row.currentStatus === 'ERRO').length;
      if (errors) return `${rows.length} (${errors} em erro)`;
      return String(rows.length);
    },
    destinationDocumentSummary(destination) {
      const rows = this.publicationsOf(destination, 'PROCUREMENT_DOCUMENT');
      if (!rows.length) return 'sem publicação';
      return String(rows.length);
    },
    statusType(status) {
      if (status === 'INTEGRADO') return 'is-success';
      if (status === 'ERRO') return 'is-danger';
      if (status === 'PENDENTE' || status === 'PROCESSANDO') return 'is-warning';
      return 'is-light';
    },
    inboxStatusType(status) {
      if (status === 'DESPACHADO') return 'is-success';
      if (status === 'FALHOU' || status === 'REJEITADO') return 'is-danger';
      return 'is-warning';
    },
    reprocessKey(destination, resourceType, entityId) {
      return `${destination}:${resourceType}:${entityId}`;
    },
    isReprocessing(destination, resourceType, entityId) {
      return this.reprocessingKey === this.reprocessKey(destination, resourceType, entityId);
    },
    isBatchReprocessing(destination, resourceType) {
      return this.batchReprocessingKey === `${destination}:${resourceType}`;
    },
    publicationFromDestination(destination) {
      return {
        destination: destination.destination,
        resourceType: 'PROCUREMENT',
        entityId: this.dossier.procurement.id,
        parentEntityId: null,
        supported: destination.supported,
        currentStatus: destination.currentStatus,
        externalId: destination.externalId,
      };
    },
    getErrorMessage(error) {
      if (error && error.response && error.response.data) {
        const { data } = error.response;
        if (typeof data === 'string') return data;
        if (data.message) return data.message;
        if (data.errors && data.errors.length && data.errors[0].detail) {
          return data.errors[0].detail;
        }
        if (data.error) return data.error;
        return JSON.stringify(data);
      }
      if (error && error.message) return error.message;
      return 'Não foi possível concluir a operação.';
    },
    async handleSearch() {
      const id = Number(this.procurementId);
      if (!Number.isInteger(id) || id <= 0) {
        this.$alerta('Informe um ID de pregão válido.', 'is-danger');
        return;
      }

      this.stopStatusPoll();
      this.loading.search = true;
      try {
        this.dossier = await this.fetchProcurementDossier(id);
        if (this.$route.query.id !== String(id)) {
          this.$router.replace({ query: { id: String(id) } });
        }
        await this.loadTerms(id);
      } catch (error) {
        this.dossier = null;
        this.terms = null;
        this.$alerta(this.getErrorMessage(error), 'is-danger');
      } finally {
        this.loading.search = false;
      }
    },
    async loadTerms(id, options) {
      const silent = options && options.silent;
      if (!silent) this.loading.terms = true;
      try {
        this.terms = await this.fetchAuctionTerms(id);
      } catch (error) {
        if (!silent) {
          this.terms = null;
          this.$alerta(this.getErrorMessage(error), 'is-danger');
        }
      } finally {
        if (!silent) this.loading.terms = false;
      }
    },
    async handleRegenerateTerm(term) {
      if (!term || !term.podeRegerar || !this.dossier) return;

      const acao = term.gerado ? 'Regerar' : 'Gerar';
      const confirmed = await this.$confirmacao({
        message: `${acao} o termo de ${term.titulo.toLowerCase()} do pregão #${
          this.dossier.procurement.id
        }? O PDF atual permanece até a nova geração concluir.`,
      });
      if (!confirmed) return;

      this.regeneratingTerm = term.tipo;
      try {
        await this.regenerateAuctionTerm({
          ofertaCompraId: this.dossier.procurement.id,
          tipo: term.tipo,
        });
        this.$alerta(
          'Regeração enfileirada. O PDF atual permanece até o job concluir.',
          'is-success',
        );
        await this.loadTerms(this.dossier.procurement.id);
      } catch (error) {
        this.$alerta(this.getErrorMessage(error), 'is-danger');
      } finally {
        this.regeneratingTerm = '';
      }
    },
    async refreshDossier(options) {
      const silent = options && options.silent;
      const id = Number(this.procurementId);
      if (!Number.isInteger(id) || id <= 0) return false;

      if (!silent) this.loading.search = true;
      try {
        this.dossier = await this.fetchProcurementDossier(id);
        if (!silent) await this.loadTerms(id);
        return true;
      } catch (error) {
        if (!silent) {
          this.dossier = null;
          this.terms = null;
          this.$alerta(this.getErrorMessage(error), 'is-danger');
        }
        return false;
      } finally {
        if (!silent) this.loading.search = false;
      }
    },
    stopStatusPoll() {
      if (this.statusPollTimer) {
        window.clearTimeout(this.statusPollTimer);
        this.statusPollTimer = null;
      }
      this.statusPollToken += 1;
      this.loading.poll = false;
    },
    publicationsStillInFlight(destination, resourceType, entityIds) {
      const watchedIds = entityIds && entityIds.length ? entityIds : null;
      return this.publications.some(row => {
        if (row.destination !== destination || row.resourceType !== resourceType) {
          return false;
        }
        if (watchedIds && watchedIds.indexOf(row.entityId) === -1) {
          return false;
        }
        return IN_FLIGHT_STATUSES.indexOf(row.currentStatus) !== -1;
      });
    },
    startStatusPoll(destination, resourceType, entityIds) {
      this.stopStatusPoll();
      const token = this.statusPollToken;
      const deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
      this.loading.poll = true;

      const tick = async () => {
        if (token !== this.statusPollToken) return;

        await this.refreshDossier({ silent: true });
        if (token !== this.statusPollToken) return;

        const stillInFlight = this.publicationsStillInFlight(destination, resourceType, entityIds);
        if (!stillInFlight || Date.now() >= deadline) {
          this.loading.poll = false;
          this.statusPollTimer = null;
          return;
        }

        this.statusPollTimer = window.setTimeout(tick, STATUS_POLL_INTERVAL_MS);
      };

      this.statusPollTimer = window.setTimeout(tick, STATUS_POLL_INTERVAL_MS);
    },
    async handleReprocess(row) {
      if (!row || !row.supported) return;

      const destination = this.destinationLabel(row.destination);
      const resource = row.label || this.resourceLabel(row.resourceType);
      const scope =
        row.resourceType === 'PROCUREMENT'
          ? 'apenas a compra (sem itens nem documentos)'
          : `${resource} #${row.entityId}`;
      const confirmed = await this.$confirmacao({
        message: `Reenviar ${destination} · ${scope}?`,
      });
      if (!confirmed) return;

      this.reprocessingKey = this.reprocessKey(row.destination, row.resourceType, row.entityId);

      const payload = {
        destination: row.destination,
        resourceType: row.resourceType,
        entityId: row.entityId,
      };
      if (row.parentEntityId) {
        payload.parentEntityId = row.parentEntityId;
      }

      try {
        const result = await this.reprocessIntegration(payload);
        const operation = result && result.operation ? result.operation : '';
        this.$alerta(
          operation ? `Reprocesso ${operation} enfileirado.` : 'Reprocesso enfileirado.',
          'is-success',
        );
        await this.refreshDossier({ silent: true });
        this.startStatusPoll(row.destination, row.resourceType, [row.entityId]);
      } catch (error) {
        this.$alerta(this.getErrorMessage(error), 'is-danger');
      } finally {
        this.reprocessingKey = '';
      }
    },
    async handleBatchReprocess(destination, resourceType, onlyWithError) {
      const resource = this.resourceLabel(resourceType).toLowerCase();
      const entityLabel =
        resourceType === 'PROCUREMENT_DOCUMENT' ? 'documentos da compra' : 'itens da compra';
      const scope = onlyWithError ? 'apenas os itens com erro' : `todos os ${entityLabel}`;
      const confirmed = await this.$confirmacao({
        message: `Reenviar ${this.destinationLabel(destination)} · ${scope} do pregão #${
          this.dossier.procurement.id
        }? A compra em si não será reprocessada neste passo.`,
      });
      if (!confirmed) return;

      this.batchReprocessingKey = `${destination}:${resourceType}`;
      try {
        const result = await this.reprocessIntegrationBatch({
          destination,
          resourceType,
          parentEntityId: this.dossier.procurement.id,
          onlyWithError,
        });
        const enqueued = (result && result.enqueued) || [];
        const failed = (result && result.failed) || [];
        if (!enqueued.length && !failed.length) {
          this.$alerta(
            `Nenhuma publicação de ${resource} encontrada para reprocessar.`,
            'is-warning',
          );
        } else if (failed.length && !enqueued.length) {
          this.$alerta(`Nenhum ${resource} foi enfileirado.`, 'is-danger');
        } else if (failed.length) {
          this.$alerta(
            `${enqueued.length} enfileirado(s), ${failed.length} falhou/falharam.`,
            'is-warning',
          );
        } else {
          this.$alerta(`${enqueued.length} ${resource}(s) enfileirado(s).`, 'is-success');
        }
        if (enqueued.length) {
          await this.refreshDossier({ silent: true });
          this.startStatusPoll(
            destination,
            resourceType,
            enqueued.map(item => item.entityId),
          );
        }
      } catch (error) {
        this.$alerta(this.getErrorMessage(error), 'is-danger');
      } finally {
        this.batchReprocessingKey = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.mb-0 {
  margin-bottom: 0;
}

.mb-2 {
  margin-bottom: 0.5rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.destination-last-error {
  display: block;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 10rem;
  overflow-y: auto;
}
</style>
